// ─── Template : FEUILLE DE FILTRES (Core) ─────────────────────────
// Motif app-wide repris du calendrier : la page ne montre qu'une BARRE
// compacte ; le choix se fait ici, en CASES À COCHER (multi-sélection) et en
// BROUILLON — rien ne s'applique tant qu'on n'a pas touché la 💾 (grisée si
// rien n'a changé). ↺ réinitialise (tout coché = aucun filtre).
//
//   <FeuilleFiltres visible groupes={GROUPES} valeurs={f} onAppliquer={setF} onFermer={…} />
//   GROUPES = [{ cle:'plan', titre:'Forfait', emoji:'🏷️',
//                options:[{ id:'gratuit', label:'Gratuit' }, …] }]
//   valeurs[cle] = TABLEAU des ids COCHÉS. Absent ⇒ tout coché (pas de filtre).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Feuille from './Feuille';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const tousIds = (g) => (g.options || []).map(o => o.id);
// ABSENT = aucun filtre (tout coché). Un tableau VIDE est un choix explicite
// « rien de ce groupe » — on ne le confond pas avec l'absence.
const cochesDe = (valeurs, g) => {
  const v = valeurs?.[g.cle];
  return Array.isArray(v) ? v : tousIds(g);
};

export default function FeuilleFiltres({ visible, groupes = [], valeurs = {}, onAppliquer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [brouillon, setBrouillon] = useState({});

  useEffect(() => {
    if (!visible) return;
    setBrouillon(Object.fromEntries(groupes.map(g => [g.cle, cochesDe(valeurs, g)])));
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const memeListe = (a, b) => a.length === b.length && a.every(x => b.includes(x));
  const modifie = groupes.some(g => !memeListe(brouillon[g.cle] || [], cochesDe(valeurs, g)));
  const reinitialiser = () => setBrouillon(Object.fromEntries(groupes.map(g => [g.cle, tousIds(g)])));

  const basculer = (g, id) => setBrouillon(b => {
    const cur = b[g.cle] || tousIds(g);
    const suiv = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    return { ...b, [g.cle]: suiv };
  });
  // Tout cocher / tout décocher d'un groupe (l'en-tête du groupe fait office
  // de bascule globale, comme le « tout » du calendrier).
  const toutBasculer = (g) => setBrouillon(b => {
    const cur = b[g.cle] || tousIds(g);
    return { ...b, [g.cle]: cur.length === tousIds(g).length ? [] : tousIds(g) };
  });

  return (
    <Feuille visible={visible} titre={t('filtres.titre')} emoji="🏷️" hauteur={0.7} onFermer={onFermer}
      action={{ icone: '💾', label: t('commun.enregistrer'), disabled: !modifie,
        onPress: () => {
          // Un groupe entièrement coché = AUCUN filtre : on retire la clé au
          // lieu de stocker la liste complète (sinon la barre le croit actif
          // et un groupe à une seule option filtrerait tout).
          const sortie = { ...valeurs };
          groupes.forEach(g => {
            const c = brouillon[g.cle] || [];
            if (c.length === tousIds(g).length) delete sortie[g.cle];
            else sortie[g.cle] = c;
          });
          onAppliquer?.(sortie); onFermer?.();
        } }}
      boutons={(
        <TouchableOpacity hitSlop={HIT} onPress={reinitialiser} accessibilityLabel={t('filtres.reinitialiser')}>
          <Text style={s.reinit}>↺</Text>
        </TouchableOpacity>
      )}>
      {groupes.map(g => {
        const coches = brouillon[g.cle] || tousIds(g);
        const tout = coches.length === tousIds(g).length;
        return (
          <View key={g.cle}>
            <TouchableOpacity style={s.entete} onPress={() => toutBasculer(g)} activeOpacity={0.7}>
              <Text style={s.lbl}>{g.emoji ? `${g.emoji} ` : ''}{g.titre}</Text>
              <Text style={s.toutTxt}>{tout ? t('filtres.toutDecocher') : t('filtres.toutCocher')}</Text>
            </TouchableOpacity>
            {(g.options || []).map(o => {
              const on = coches.includes(o.id);
              return (
                <TouchableOpacity key={o.id} style={s.ligne} onPress={() => basculer(g, o.id)} activeOpacity={0.7}>
                  <Text style={s.ligneTxt}>{o.label}</Text>
                  <Text style={[s.case_, on && s.caseOn]}>{on ? '☑' : '☐'}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        );
      })}
      <Text style={s.aide}>{t('filtres.aide')}</Text>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  entete: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 16 },
  lbl: { fontSize: 12.5, fontWeight: '800', color: UI.muted },
  toutTxt: { fontSize: 11.5, color: UI.accent, fontWeight: '700' },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9,
           borderTopWidth: 0.5, borderTopColor: UI.border },
  ligneTxt: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600', textTransform: 'capitalize' },
  case_: { fontSize: 20, color: UI.faint },
  caseOn: { color: UI.accent },
  reinit: { fontSize: 22, color: UI.accent, fontWeight: '700' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 18, lineHeight: 16 },
});
